import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Save, Trash2, GripVertical } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const INPUT = "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#27AE60]";

function newItem() {
  return { key: `item_${Math.random().toString(36).slice(2, 10)}`, label: "" };
}

export default function InterviewFormManager() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/interview-form");
        setForm(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const update = (updater) => setForm((prev) => updater(structuredClone(prev)));

  const setSection = (si, patch) => update((f) => { Object.assign(f.sections[si], patch); return f; });
  const setItem = (si, gi, ii, label) => update((f) => {
    const list = gi === null ? f.sections[si].items : f.sections[si].groups[gi].items;
    list[ii].label = label;
    return f;
  });
  const addItem = (si, gi) => update((f) => {
    const list = gi === null ? f.sections[si].items : f.sections[si].groups[gi].items;
    list.push(newItem());
    return f;
  });
  const removeItem = (si, gi, ii) => update((f) => {
    const list = gi === null ? f.sections[si].items : f.sections[si].groups[gi].items;
    list.splice(ii, 1);
    return f;
  });
  const setGroup = (si, gi, patch) => update((f) => { Object.assign(f.sections[si].groups[gi], patch); return f; });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/interview-form", form);
      setForm(data);
      toast.success("Form Wawancara berhasil disimpan.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#27AE60]" /></div>;
  }
  if (!form) return null;

  return (
    <div className="space-y-6" data-testid="interview-form-manager">
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h2 className="font-display text-xl font-extrabold text-[#1F2937]">Form Wawancara</h2>
          <p className="text-sm text-[#6B7280]">Kelola seluruh isian formulir penilaian wawancara yang digunakan pewawancara.</p>
        </div>
        <button type="button" onClick={save} disabled={saving} data-testid="save-interview-form"
          className="flex items-center gap-2 rounded-xl bg-[#27AE60] px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#0B6B3A] disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Simpan Form
        </button>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-5">
        <label className="mb-1.5 block text-sm font-semibold text-[#1F2937]">Judul Form</label>
        <input value={form.title} onChange={(e) => update((f) => { f.title = e.target.value; return f; })}
          data-testid="form-title-input" className={INPUT} />
      </div>

      {form.sections.map((section, si) => (
        <div key={section.id} className="rounded-2xl border border-gray-100 bg-white p-5 space-y-4" data-testid={`section-editor-${section.id}`}>
          <div className="grid gap-3 sm:grid-cols-[1fr_120px]">
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase text-[#6B7280]">Judul Bagian</label>
              <input value={section.title} onChange={(e) => setSection(si, { title: e.target.value })} className={INPUT} data-testid={`section-title-${section.id}`} />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase text-[#6B7280]">Bobot (%)</label>
              <input type="number" min="0" max="100" value={section.weight} onChange={(e) => setSection(si, { weight: Number(e.target.value) })} className={INPUT} data-testid={`section-weight-${section.id}`} />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase text-[#6B7280]">Petunjuk</label>
            <input value={section.instruction || ""} onChange={(e) => setSection(si, { instruction: e.target.value })} className={INPUT} />
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold text-[#1F2937]">
            <input type="checkbox" checked={!!section.item_notes} onChange={(e) => setSection(si, { item_notes: e.target.checked })} className="h-4 w-4 accent-[#27AE60]" />
            Sertakan kolom Catatan per parameter
          </label>

          {section.groups ? (
            section.groups.map((group, gi) => (
              <div key={group.id} className="rounded-xl border border-gray-100 bg-[#F9FAFB] p-4 space-y-3">
                <div className="grid gap-3 sm:grid-cols-[1fr_120px]">
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase text-[#6B7280]">Judul Sub-aspek</label>
                    <input value={group.title} onChange={(e) => setGroup(si, gi, { title: e.target.value })} className={INPUT} data-testid={`group-title-${group.id}`} />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase text-[#6B7280]">Bobot (%)</label>
                    <input type="number" min="0" max="100" value={group.weight} onChange={(e) => setGroup(si, gi, { weight: Number(e.target.value) })} className={INPUT} data-testid={`group-weight-${group.id}`} />
                  </div>
                </div>
                <ItemList items={group.items} onChange={(ii, v) => setItem(si, gi, ii, v)} onAdd={() => addItem(si, gi)} onRemove={(ii) => removeItem(si, gi, ii)} />
              </div>
            ))
          ) : (
            <ItemList items={section.items} onChange={(ii, v) => setItem(si, null, ii, v)} onAdd={() => addItem(si, null)} onRemove={(ii) => removeItem(si, null, ii)} />
          )}
        </div>
      ))}

      <div className="rounded-2xl border border-gray-100 bg-white p-5 space-y-3">
        <h3 className="font-display text-base font-bold text-[#1F2937]">Pilihan Rekomendasi Kelayakan</h3>
        {form.decisions.map((option, di) => (
          <div key={di} className="flex items-center gap-2">
            <input value={option.label}
              onChange={(e) => update((f) => { f.decisions[di].label = e.target.value; return f; })}
              className={INPUT} data-testid={`decision-input-${di}`} />
            <button type="button" onClick={() => update((f) => { f.decisions.splice(di, 1); return f; })}
              className="shrink-0 rounded-lg p-2 text-[#DC2626] hover:bg-[#FEE2E2]" aria-label="Hapus rekomendasi">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        <button type="button" onClick={() => update((f) => { f.decisions.push({ value: "", label: "" }); return f; })}
          data-testid="add-decision" className="flex items-center gap-1.5 text-sm font-bold text-[#0B6B3A] hover:underline">
          <Plus className="h-4 w-4" /> Tambah Rekomendasi
        </button>
      </div>

      <button type="button" onClick={save} disabled={saving} data-testid="save-interview-form-bottom"
        className="flex items-center gap-2 rounded-xl bg-[#27AE60] px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#0B6B3A] disabled:opacity-60">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Simpan Form
      </button>
    </div>
  );
}

function ItemList({ items, onChange, onAdd, onRemove }) {
  return (
    <div className="space-y-2">
      {items.map((item, ii) => (
        <div key={item.key} className="flex items-center gap-2">
          <GripVertical className="h-4 w-4 shrink-0 text-gray-300" />
          <input value={item.label} onChange={(e) => onChange(ii, e.target.value)} placeholder="Nama parameter penilaian"
            className={INPUT} data-testid={`item-input-${item.key}`} />
          <button type="button" onClick={() => onRemove(ii)} className="shrink-0 rounded-lg p-2 text-[#DC2626] hover:bg-[#FEE2E2]" aria-label="Hapus parameter">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button type="button" onClick={onAdd} className="flex items-center gap-1.5 text-sm font-bold text-[#0B6B3A] hover:underline">
        <Plus className="h-4 w-4" /> Tambah Parameter
      </button>
    </div>
  );
}
